// src/features/players/usePlayers.ts

import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PlayerApi } from './PlayerApi';
import { PLAYER_QUERY_KEYS } from '@/lib/constants/player';
import { STORAGE_KEYS } from '@/lib/constants/storage';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { PlayerViewData } from '@/lib/types/Player';

export interface PlayerSelectOption {
  value: string;
  label: string;
}

export interface UsePlayersResult {
  players: PlayerViewData[];
  defaultPlayerId: number | null;
  playerOptions: PlayerSelectOption[];
  getPlayerById: (playerId: number) => PlayerViewData | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Get stored player ID from localStorage, scoped to current customer. */
function getStoredPlayerId(cId: number | null): number | null {
  if (!cId) return null;
  try {
    const key = `${STORAGE_KEYS.SELECTED_PLAYER_ID}_${cId}`;
    const stored = localStorage.getItem(key);
    return stored ? parseInt(stored, 10) : null;
  } catch {
    return null;
  }
}

/** Save selected player ID to localStorage, scoped to customer. */
export function setStoredPlayerId(cId: number, playerId: number): void {
  try {
    const key = `${STORAGE_KEYS.SELECTED_PLAYER_ID}_${cId}`;
    localStorage.setItem(key, String(playerId));
  } catch {
    // localStorage may be unavailable
  }
}

/**
 * Fetch all players for the current customer, sorted alphabetically by label.
 * Provides defaultPlayerId resolved in order:
 *   1. Last selected from localStorage (scoped per customer)
 *   2. First player with isDefault=true from backend
 *   3. First player in list (alphabetical)
 */
export function usePlayers(): UsePlayersResult {
  const { selectedCId } = useAuth();

  const query = useQuery({
    queryKey: PLAYER_QUERY_KEYS.list(selectedCId ?? 0),
    queryFn: async () => PlayerApi.getPlayers(selectedCId!),
    enabled: selectedCId != null,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const players = useMemo(
    () => [...(query.data ?? [])].sort((a, b) =>
      a.core.label.localeCompare(b.core.label, undefined, { sensitivity: 'base' })),
    [query.data],
  );

  const defaultPlayerId = useMemo(() => {
    if (players.length === 0) return null;

    // 1. Check localStorage for last-selected (per customer)
    const storedId = getStoredPlayerId(selectedCId);
    if (storedId && players.some(p => p.core.playerId === storedId)) {
      return storedId;
    }

    // 2. Backend isDefault flag
    const defaultPlayer = players.find(p => p.core.isDefault);
    if (defaultPlayer) return defaultPlayer.core.playerId;

    // 3. First in list
    return players[0].core.playerId;
  }, [players, selectedCId]);

  const playerOptions = useMemo<PlayerSelectOption[]>(() =>
    players.map(p => ({
      value: String(p.core.playerId),
      label: p.core.label,
    })),
    [players],
  );

  const getPlayerById = useCallback(
    (playerId: number) => players.find(p => p.core.playerId === playerId),
    [players],
  );

  return {
    players,
    defaultPlayerId,
    playerOptions,
    getPlayerById,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
